// @givmo/auth — structured auth error model.
//
// Ported from the iOS `AuthError` enum + `AuthError.from(_:)` mapping
// (givmo-ios/Givmo/Core/Error/GivmoError.swift + Features/Auth/Services/AuthService.swift) and the
// webapp's error-handler posture. The iOS source maps `FirebaseAuth.AuthErrorCode` (an Int enum) to
// a small, stable, machine-readable `AuthError`; the Firebase JS SDK instead surfaces STRING codes
// (`FirebaseError.code`, e.g. `auth/wrong-password`). This module is the JS-string equivalent of
// that switch — every consuming app branches on `AuthError.kind`, never on a raw Firebase code.
//
// WHY a hand-modelled enum and not the raw FirebaseError: the same discipline as the api-client
// error model — callers must branch on a finite, documented set of cases (cancelled, userNotFound,
// smsQuotaExceeded, …) that survives Firebase renaming/adding codes, and the `unknown` fallback
// keeps an unrecognised code from throwing an unhandled shape. PII-safe: we carry only the Firebase
// code string + the SDK's own message; we never stringify credentials, tokens, or user input.

/** The finite set of auth failure kinds. Mirrors the iOS `AuthError` enum case-for-case. */
export type AuthErrorKind =
  | 'invalidCredential'
  | 'emailAlreadyInUse'
  | 'weakPassword'
  | 'userNotFound'
  | 'userDisabled'
  | 'tooManyRequests'
  | 'phoneNumberUnlinked'
  | 'sessionExpired'
  | 'userMismatch'
  | 'cancelled'
  | 'credentialAlreadyInUse'
  | 'accountExistsWithDifferentCredential'
  | 'invalidVerificationCode'
  | 'verificationIDExpired'
  | 'smsQuotaExceeded'
  | 'appNotAuthorized'
  | 'popupBlocked'
  | 'popupClosedByUser'
  | 'networkRequestFailed'
  | 'secondFactorRequired'
  | 'passkeyCeremonyFailed'
  | 'passkeyUserVerificationRequired'
  | 'passkeyNoneRegistered'
  | 'requiresRecentLogin'
  | 'unverifiedEmail'
  | 'unsupportedFirstFactor'
  | 'secondFactorAlreadyInUse'
  | 'noAuthenticatorToRemove'
  | 'organizationSsoRequired'
  | 'unknown';

/**
 * The Firebase code raised when the FIRST factor succeeded but the account carries an enrolled
 * second factor. Named because two modules key off it: the mapping below, and `mfa.ts`, which reads
 * the retained source error back out to build the resolver.
 */
export const SECOND_FACTOR_REQUIRED_CODE = 'auth/multi-factor-auth-required';

/**
 * The company's sign-in lock refused a personal sign-in (AS-WEB27): the notice's heading and first
 * line, DRAFT (D-AS-48 (4)) — the signed-out twin of the apps' "{company} manages how you sign in",
 * and iOS lockBody's first sentence without its "use the Givmo app" (the web has its own company door,
 * AS-WEB30). A console renders them as its lock notice (`@givmo/passkeys`' `LockRefusalNotice`, with
 * the way back in); a surface without one (donate, consent) shows them as its error line. Signed out,
 * they name no company.
 */
export const SIGN_IN_LOCK_TITLE = 'Your company manages how you sign in';
export const SIGN_IN_LOCK_LINE =
  'Your employer requires you to sign in with your work account, so your other ways of signing in are turned off for now.';

/** Default user-facing copy per kind. Ported from the iOS `AuthError.userMessage` switch. */
const USER_MESSAGES: Record<AuthErrorKind, string> = {
  invalidCredential: 'Invalid email or password.',
  emailAlreadyInUse: 'This email is already registered.',
  weakPassword: 'Password is too weak.',
  userNotFound: 'No account found with this email.',
  userDisabled: 'This account has been disabled.',
  tooManyRequests: 'Too many attempts. Please try again later.',
  phoneNumberUnlinked: 'Please link a phone number to continue.',
  sessionExpired: 'Your session has expired. Please sign in again.',
  // Re-authentication only (`reauth.ts`): the credential was valid but belongs to somebody else, so
  // "invalid" would be a lie and would send the user to reset a password that is fine. `reauth.ts`
  // overrides this with copy naming the account they must verify as, which it knows and this doesn't.
  userMismatch:
    'That’s a different account. Confirm as the account you’re signed in with to continue.',
  // `cancelled` is intentionally empty — a user-initiated cancel is not an error to surface.
  cancelled: '',
  credentialAlreadyInUse: 'This account is already linked to another sign-in method.',
  accountExistsWithDifferentCredential:
    'An account already exists with this email using a different sign-in method.',
  // Decision 1 of D-AS-35: the "didn't work" family. On the web this default reaches only a TEXTED
  // code (every authenticator-code step — the sign-in prompts, the sheet, setup — words its own).
  invalidVerificationCode: 'That code didn’t work. Check the code we texted you and try again.',
  verificationIDExpired: 'That code has expired. Get a new code and try again.',
  smsQuotaExceeded: 'Too many SMS requests. Please try again later.',
  appNotAuthorized:
    'Phone verification is not available right now. Please try a different sign-in method.',
  popupBlocked: 'The sign-in popup was blocked. Please allow popups and try again.',
  popupClosedByUser: 'Sign-in was cancelled before it completed.',
  networkRequestFailed: 'No internet connection. Please check your connection and try again.',
  // Deliberately neutral: an app that renders the challenge never shows this string (it prompts for
  // a code instead), so this is what an app WITHOUT challenge UI shows — where promising a code
  // prompt would be a lie. `mfa.ts` overrides it for the factor kinds the web cannot complete.
  secondFactorRequired: 'Two-factor authentication is required to finish signing in.',
  // Passkey sign-in (`passkeys.ts`). The backend answers every failed assertion with ONE machine code
  // on purpose — an assertion that fails because no such credential exists must be indistinguishable
  // from one that fails because the signature was wrong, or the response would enumerate accounts. So
  // this copy names a remedy and claims nothing about WHY, which is all the server itself will say.
  // Decision 1 of D-AS-35 (a passkey that fails at sign-in).
  passkeyCeremonyFailed: 'That passkey didn’t work. Try again, or sign in another way.',
  // The one refusal the backend separates out, because the user can act on it. Deliberately does not
  // auto-retry: a second ceremony needs its own user gesture, and silently re-prompting for a
  // fingerprint nobody asked for is worse than saying what is needed.
  passkeyUserVerificationRequired:
    'Your passkey needs to confirm it’s you. Try again and complete the fingerprint, face, or PIN check.',
  // The account-bound re-confirmation (`passkey-reconfirmation.ts`) on an account that holds no
  // passkey — the backend's own approved sentence (D-AS-29, C4). The step-up sheet never shows it:
  // it routes the account to its own way in instead.
  passkeyNoneRegistered: 'This account has no passkey to confirm with.',
  // Authenticator-app enrolment and removal (`totp-enrollment.ts`, AS-WEB14). Identity Platform's
  // own freshness and first-factor rules; the console's "Confirm it's you" sheet answers the first and
  // third before the person reads these, so each is the honest fallback (approved, D-AS-35 D6; the
  // consoles offer no Apple sign-in, so the first-factor line names Google and Microsoft only).
  requiresRecentLogin: 'This change needs a recent sign-in. Confirm it’s you, then try again.',
  unverifiedEmail: 'Verify your email address before setting up an authenticator app.',
  unsupportedFirstFactor:
    'An authenticator app can be set up only after signing in with Google or Microsoft.',
  secondFactorAlreadyInUse: 'An authenticator app is already set up on this account.',
  // Turning off found no authenticator on the account once Firebase's record was reloaded (cut 1003c's
  // review, F3): it was already removed elsewhere. Never a silent "done" (approved, D-AS-35 D6).
  noAuthenticatorToRemove: 'Your authenticator app is already off.',
  // The sign-in lock's refusal (AS-WEB27): never the server's raw words (see `isSignInLockRefusal`).
  organizationSsoRequired: `${SIGN_IN_LOCK_TITLE}. ${SIGN_IN_LOCK_LINE}`,
  unknown: 'Something went wrong. Please try again.',
};

/**
 * Firebase JS SDK error-code string → `AuthErrorKind`.
 *
 * Mirrors the iOS `AuthError.from(_:)` switch, translated from the Swift `AuthErrorCode` Int enum to
 * the JS SDK's `auth/*` string codes. `auth/invalid-credential` + `auth/wrong-password` collapse to
 * `invalidCredential` (matching iOS); `auth/invalid-verification-id` + `auth/code-expired` +
 * `auth/session-expired` collapse to `verificationIDExpired`; `auth/quota-exceeded` +
 * `auth/missing-phone-number` collapse to `smsQuotaExceeded`.
 */
const FIREBASE_CODE_TO_KIND: Record<string, AuthErrorKind> = {
  'auth/invalid-credential': 'invalidCredential',
  'auth/wrong-password': 'invalidCredential',
  'auth/invalid-email': 'invalidCredential',
  'auth/email-already-in-use': 'emailAlreadyInUse',
  'auth/weak-password': 'weakPassword',
  'auth/user-not-found': 'userNotFound',
  'auth/user-disabled': 'userDisabled',
  'auth/too-many-requests': 'tooManyRequests',
  'auth/credential-already-in-use': 'credentialAlreadyInUse',
  'auth/account-exists-with-different-credential': 'accountExistsWithDifferentCredential',
  'auth/invalid-verification-code': 'invalidVerificationCode',
  'auth/missing-verification-code': 'invalidVerificationCode',
  'auth/invalid-verification-id': 'verificationIDExpired',
  'auth/code-expired': 'verificationIDExpired',
  'auth/session-expired': 'verificationIDExpired',
  // Second-factor challenge (`mfa.ts`). The two *-multi-factor-session codes mean the short-lived
  // pending credential behind an in-flight challenge is gone, so the only way forward is a fresh
  // sign-in — which is exactly what `sessionExpired`'s copy tells the user to do.
  [SECOND_FACTOR_REQUIRED_CODE]: 'secondFactorRequired',
  'auth/invalid-multi-factor-session': 'sessionExpired',
  'auth/missing-multi-factor-session': 'sessionExpired',
  // Raised only by the `reauthenticate*` primitives, when the credential given is valid but belongs
  // to a DIFFERENT account than the one being re-verified — the identity binding those primitives
  // exist to enforce, and therefore the one refusal a step-up surface must word precisely.
  'auth/user-mismatch': 'userMismatch',
  'auth/quota-exceeded': 'smsQuotaExceeded',
  'auth/missing-phone-number': 'smsQuotaExceeded',
  'auth/app-not-authorized': 'appNotAuthorized',
  // Popup-first specifics (no iOS analogue — iOS uses native presentation, the web uses popups).
  'auth/popup-blocked': 'popupBlocked',
  'auth/popup-closed-by-user': 'popupClosedByUser',
  'auth/cancelled-popup-request': 'cancelled',
  'auth/user-cancelled': 'cancelled',
  'auth/network-request-failed': 'networkRequestFailed',
  // Authenticator-app enrolment and removal (`totp-enrollment.ts`).
  'auth/requires-recent-login': 'requiresRecentLogin',
  'auth/unverified-email': 'unverifiedEmail',
  'auth/unsupported-first-factor': 'unsupportedFirstFactor',
  'auth/second-factor-already-in-use': 'secondFactorAlreadyInUse',
};

/** The JS SDK's code for a blocking function's refusal, which it shares with genuine internal errors. */
const INTERNAL_ERROR_CODE = 'auth/internal-error';

/** The first brace-balanced `{…}` in a string, parsed; `null` when there is none or it is not JSON. */
function embeddedJson(text: string): unknown {
  const start = text.indexOf('{');
  if (start === -1) return null;
  let depth = 0;
  let inString = false;
  for (let i = start; i < text.length; i += 1) {
    const ch = text[i];
    if (inString) {
      if (ch === '\\') i += 1;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') inString = true;
    else if (ch === '{') depth += 1;
    else if (ch === '}') {
      depth -= 1;
      if (depth === 0) {
        try {
          return JSON.parse(text.slice(start, i + 1));
        } catch {
          return null;
        }
      }
    }
  }
  return null;
}

/**
 * Is this Firebase failure the company's SIGN-IN LOCK refusing a personal sign-in (AS-WEB27)?
 *
 * Every Firebase-side sign-in (password, Google, Microsoft, Apple, Facebook, phone, and the second
 * factor that completes one) passes the project's `beforeSignIn` blocking function, which refuses a
 * locked account with `HttpsError(PERMISSION_DENIED, REJECTION_MESSAGE)` (the backend's
 * `functions/main.py`). Identity Toolkit answers `BLOCKING_FUNCTION_ERROR_RESPONSE : <the function's
 * error>`, and `@firebase/auth` maps that code to **`auth/internal-error`** with the rest as the
 * message (`_performFetchWithErrorHandling` → `_errorWithCustomMessage`, read in 1.13.3), so the web
 * sees only that code and a message embedding `{"error":{"message":…,"status":"PERMISSION_DENIED"}}`.
 *
 * Unlike iOS (code 17105, blocking functions only) the code alone cannot route here: it is shared
 * with genuine internal errors. The key is the code AND the embedded status. This project registers
 * exactly one blocking function, the lock (the iOS detector's own argument), so a PERMISSION_DENIED
 * from one IS the lock. The words are never read: the backend's message is DRAFT and names the apps'
 * buttons, and the web shows its own notice instead (D-AS-48 5a).
 *
 * ⚠ Read from the SDK source, not measured: no environment deploys the blocking function yet.
 */
function isSignInLockRefusal(code: string, error: unknown): boolean {
  if (code !== INTERNAL_ERROR_CODE) return false;
  const message = extractMessage(error);
  if (message === undefined) return false;
  const body = embeddedJson(message) as { error?: { status?: unknown } } | null;
  return body?.error?.status === 'PERMISSION_DENIED';
}

/**
 * Side table holding the ORIGINAL thrown value for the kinds that need it — today only
 * `secondFactorRequired`, because `getMultiFactorResolver` accepts nothing but the Firebase
 * `MultiFactorError` object itself, and by the time a screen catches the failure every sign-in path
 * has already normalised it through {@link AuthError.from}.
 *
 * A module-private `WeakMap` rather than a field so the raw error is invisible to `JSON.stringify`,
 * console logging, and Sentry's error serialisation: a `MultiFactorError` embeds the
 * `mfaPendingCredential` bearer token, which must never leave the browser. Entries die with the
 * `AuthError` that keys them.
 */
const SOURCE_ERRORS = new WeakMap<AuthError, unknown>();

/**
 * Structured auth error. Carries the resolved `kind`, the original Firebase `code` (when present,
 * for telemetry/correlation), and a user-facing `message`. Extends `Error` so it can be `throw`n and
 * caught with `instanceof AuthError`.
 */
export class AuthError extends Error {
  readonly kind: AuthErrorKind;
  /** The original Firebase `auth/*` code string, when the source was a FirebaseError. */
  readonly code?: string;

  constructor(kind: AuthErrorKind, options?: { code?: string; message?: string }) {
    super(options?.message ?? USER_MESSAGES[kind]);
    this.name = 'AuthError';
    this.kind = kind;
    this.code = options?.code;
    // Restore the prototype chain so `instanceof AuthError` holds after transpilation to ES targets
    // that down-level `extends Error`.
    Object.setPrototypeOf(this, AuthError.prototype);
  }

  /** User-facing copy for a kind (the default message). */
  static userMessage(kind: AuthErrorKind): string {
    return USER_MESSAGES[kind];
  }

  /**
   * The raw value this error was normalised from, when one was retained (see {@link SOURCE_ERRORS}).
   * `undefined` for every kind but `secondFactorRequired`. Read it only to hand back to the SDK —
   * never to log, render, or serialise.
   */
  static sourceOf(error: AuthError): unknown {
    return SOURCE_ERRORS.get(error);
  }

  /**
   * Normalise any thrown value into an `AuthError`. The JS analogue of iOS `AuthError.from(_:)`.
   *
   * - An existing `AuthError` passes through unchanged (idempotent).
   * - A Firebase-shaped error (an object with a string `code`) maps via `FIREBASE_CODE_TO_KIND`;
   *   an unrecognised `auth/*` code becomes `unknown` but RETAINS the code for telemetry.
   * - Anything else becomes `unknown`.
   */
  static from(error: unknown): AuthError {
    if (error instanceof AuthError) {
      return error;
    }
    const code = extractFirebaseCode(error);
    if (code !== undefined) {
      if (isSignInLockRefusal(code, error)) {
        return new AuthError('organizationSsoRequired', { code });
      }
      const kind = FIREBASE_CODE_TO_KIND[code] ?? 'unknown';
      // For an unmapped code, keep the SDK's own message (still PII-safe — Firebase codes don't
      // embed credentials); for a mapped kind, use our canonical copy.
      const message =
        kind === 'unknown' ? (extractMessage(error) ?? USER_MESSAGES.unknown) : undefined;
      const authError = new AuthError(kind, { code, message });
      if (kind === 'secondFactorRequired') {
        // Retain the resolver-bearing original so `mfa.ts` can still build the challenge after this
        // normalisation — every sign-in path funnels through here before a screen sees the failure.
        SOURCE_ERRORS.set(authError, error);
      }
      return authError;
    }
    return new AuthError('unknown', { message: extractMessage(error) });
  }
}

/** Reads a string `code` off an unknown error (FirebaseError shape) without throwing. */
function extractFirebaseCode(error: unknown): string | undefined {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const code = (error as { code: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

/** Reads a string `message` off an unknown error without throwing. */
function extractMessage(error: unknown): string | undefined {
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message: unknown }).message;
    return typeof message === 'string' && message.length > 0 ? message : undefined;
  }
  return undefined;
}
